import { useState, useMemo } from 'react';

import {
  $createParagraphNode,
  $getRoot,
  $getSelection,
  $isRangeSelection,
  LexicalEditor,
} from 'lexical';

import { useValidationService } from '../../../../../services/hooks';
import { Dialog, DialogContent } from '../../../../../Dialog';
import { Button } from '../../../../../Button';
import { TextInput } from '../../../../../input';
import { $createQuoteNode } from '../../../nodes/QuoteNode';
import { useForm } from '../../hooks';
import { getSelectedNode } from '../../../utils';

interface QuoteDialogProps {
  editor: LexicalEditor;
  open: boolean;
  onClose: () => void;
  selectionText?: string;
  authorText?: string;
  handleSubmit?: (author: string, text: string) => void;
  edit?: boolean;
}

interface QuoteDialogForm {
  quote: string;
}

export const QuoteDialog = ({
  editor,
  open,
  onClose,
  selectionText,
  authorText = '',
  handleSubmit,
  edit = false,
}: QuoteDialogProps) => {
  const [quote, setQuote] = useState(selectionText || '');
  const [author, setAuthor] = useState(authorText);
  const validator = useValidationService();

  const allValues = useMemo(() => {
    return {
      quote,
    };
  }, [quote]);

  const fieldValidators = useMemo(() => {
    return {
      quote: [validator.required<QuoteDialogForm>('Обязательное поле')],
    };
  }, [validator]);

  const { errors, touched, validateField, hasValidationErrors, handleBlur } =
    useForm<QuoteDialogForm>(allValues, fieldValidators);

  const validatorsByEditStatus = edit
    ? hasValidationErrors || (authorText === author && selectionText === quote)
    : hasValidationErrors;
  const headerTextByEditStatus = edit
    ? 'Редактирование цитаты'
    : 'Добавление цитаты';
  const buttonTextByEditStatus = edit ? 'Сохранить' : 'Добавить';

  const handleChangeQuoteInput = async (value?: string) => {
    setQuote(() => value || '');
    await validateField('quote', value);
  };

  const handleChangeAuthorInput = (value?: string) => {
    setAuthor(() => value || '');
  };

  const onSubmit = () => {
    if (handleSubmit) {
      handleSubmit(author, quote);
      onClose();
      return;
    }

    editor.update(() => {
      const selection = $getSelection();

      if (!$isRangeSelection(selection)) {
        const quoteNode = $createQuoteNode(quote, author);
        const paragraphNode = $createParagraphNode();
        $getRoot().append(quoteNode, paragraphNode);
        paragraphNode.select();
        onClose();
        return;
      }

      const selectedNode = getSelectedNode(selection);
      const parentNode = selectedNode.getParent();

      let targetBlockNode = null;

      if (
        selectedNode.getType() === 'reference-node' ||
        selectedNode.getType() === 'code-block-node'
      ) {
        targetBlockNode = selectedNode;
      } else if (
        parentNode?.getType() === 'reference-node' ||
        parentNode?.getType() === 'code-block-node'
      ) {
        targetBlockNode = parentNode;
      }

      if (targetBlockNode) {
        const extractedText = selectionText || selection.getTextContent();

        selection.insertText('');

        const quoteNode = $createQuoteNode(extractedText, author);

        targetBlockNode.insertAfter(quoteNode);

        const paragraphNode = $createParagraphNode();

        quoteNode.insertAfter(paragraphNode);
        paragraphNode.select();

        if (targetBlockNode.getTextContent().trim() === '') {
          targetBlockNode.remove();
        }

        onClose();
        return;
      }

      const quoteText = selectionText || quote;
      const quoteNode = $createQuoteNode(quoteText, author);
      const paragraphNode = $createParagraphNode();

      selection.insertNodes([quoteNode]);
      quoteNode.insertAfter(paragraphNode);
      paragraphNode.select();

      onClose();
    });
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <form onSubmit={onSubmit}>
        <DialogContent
          title={headerTextByEditStatus}
          onClose={onClose}
          actions={
            <>
              <Button
                size="large"
                variant="tertiaryLight"
                type="button"
                onClick={onClose}
              >
                Отменить
              </Button>
              <Button
                disabled={validatorsByEditStatus}
                size="large"
                variant="primary"
                type="submit"
              >
                {buttonTextByEditStatus}
              </Button>
            </>
          }
        >
          <TextInput
            label="Цитата"
            required
            placeholder="Введите текст"
            value={quote}
            onChange={handleChangeQuoteInput}
            name="text"
            error={touched.quote && !!errors.quote}
            helperText={touched.quote ? errors.quote : undefined}
            onBlur={handleBlur('quote')}
          />

          <TextInput
            label="Автор"
            placeholder="Добавьте автора цитаты"
            value={author}
            onChange={handleChangeAuthorInput}
            clearable
            name="link"
          />
        </DialogContent>
      </form>
    </Dialog>
  );
};
